/**
 * Component: PurchaseDetails.jsx
 * Description: This component is utilized for creating and updating the purchase details of an asset.
 */

import { Box, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  gridValues,
  mandatoryFields,
  updatePurchaseDetails,
} from "../../redux/actions/assetActions";
import BottomButtons from "../commoncomponents/BottomButtons";
import ComponentHeader from "../commoncomponents/ComponentHeader";
import CustomDatePicker from "../commoncomponents/CustomDatePicker";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import CustomTextField from "../commoncomponents/CustomTextField";
import CustomTextFieldWithSearch from "../commoncomponents/CustomTextFieldWithSearch";
import {
  getAuthToken,
  getFacilityId,
  getcompanyId,
} from "../../utility/tokenUtility";
import axios from "axios";
import {
  BemsGraph,
  FemsGraph,
  MasterGraph,
  ifacSecurityURL,
} from "../../utility/apiURLS";
import assetCreateUpdateRepository from "../../api/repositories/assetCreateUpdateRepository";
import AssetRepository from "../../api/repositories/assetRepository";
import PageLoader from "../commoncomponents/PageLoader";

const PurchaseDetails = ({ setPage, page, buttonAttributes }) => {
  //get the purchase details form redux
  const purchaseDetails = useSelector((state) => state?.purchaseDetails);

  //get the selected grid data from redux
  const selectedGridData = useSelector(
    (state) => state?.gridValues?.selectedGridData
  );

  //get the mandatory fileds for purchase warrenty from the redux
  const mandatoryFieldsPurchaseDetails = useSelector(
    (state) => state?.mandatoryFields?.mandatoryFieldsPurchaseDetails
  );

  //get the basicDetails values form redux
  const basicDetails = useSelector((state) => state.basicDetails);

  const dispatch = useDispatch();

  const [isDataChanged, setIsDataChanged] = useState(false);

  const findHeaderContent = buttonAttributes.find(
    (item) => item?.currentPageName === page
  );

  //get the facility id from local storage
  const facilityId = getFacilityId("facilityId");

  //get the company id from local storage
  const companyId = getcompanyId("companyId");

  const [error, setError] = useState({
    warrantyStartDate: false,
    warrantyEndDate: false,
    commissioningDate: false,
    purchaseCost: false,
    purchaseFlag: false,
    assetTransferDepartment: false,
    // vendor: false,
  });

  const [errorMessage, setErrorMessage] = useState({
    warrantyStartDate: "",
    warrantyEndDate: "",
    commissioningDate: "",
    purchaseCost: "",
    purchaseFlag: "",
    assetTransferDepartment: "",
    // vendor: '',
  });

  const [purchaseDetailsData, setPurchaseDetailsData] = useState(
    { ...purchaseDetails } || {
      warrantyStartDate: "",
      warrantyEndDate: "",
      commissioningDate: "",
      warranty: "",
      purchaseOrderNo: "",
      purchaseCost: "",
      accessories: "",
      cumpartscost: "",
      cumulativeLabourCost: "",
      lastunscheduledworkorder: "",
      lastscheduledworkorder: "",
      assetTransferCategory: "",
      purchaseFlag: "",
      assetTransferDepartment: "",
      vendor: "",
      depreciationValue: "",
      underWaranty: "",
      supplier:""
    }
  );
  const assetCategoryType = useSelector(
    (state) => state?.serviceType?.assetType
  );
  const assetType = assetCategoryType === "/assetsFEM" ? FemsGraph : BemsGraph;

  const [initialPurchaseDetailsData, setInitialPurchaseDetailsData] = useState(
    {}
  );
  const [apiDepartmentList, setApiDepartmentList] = useState([]);
  const [transferHospitalList, setTransferHospitalList] = useState([]);
  const [purchaseDetailsOptions, setPurchaseDetailsOptions] = useState({
    purchaseFlagOptions: [],
    assetTransferCategoryOptions: [],
    assetTransferDepartmentOptions: [],
    transferDepartmentList: [],
    vendorOptions: [],
  });
  const [assetTransferLabel, setAssetTransferLabel] = useState("");
  const [isFilledAllMandatoryField, setIsFilledAllMandatoryField] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const showassetSpecification = basicDetails?.assetClassification?.label;

  const checkIsUnderWarranty = () => {
    const warrantyStartDate = purchaseDetailsData?.warrantyStartDate;
    const warrantyEndDate = purchaseDetailsData?.warrantyEndDate;
    
    if (warrantyStartDate && warrantyEndDate) {
      const formattedWarrantyStartDate = dayjs(warrantyStartDate, "DD/MM/YYYY").startOf('day');
      const formattedWarrantyEndDate = dayjs(warrantyEndDate, "DD/MM/YYYY").startOf('day');
      const currentDate = dayjs().startOf('day');  
      if (
        formattedWarrantyStartDate.isSame(formattedWarrantyEndDate && formattedWarrantyEndDate.isAfter(currentDate)) ||
        formattedWarrantyEndDate.isSame(currentDate) ||
        (formattedWarrantyStartDate.isBefore(formattedWarrantyEndDate) &&
         formattedWarrantyEndDate.isAfter(currentDate))
      ) {
        setPurchaseDetailsData((prev) => ({
          ...prev,
          underWarrenty: true,
        }));
      } else {  
        setPurchaseDetailsData((prev) => ({
          ...prev,
          underWarrenty: false,
        }));
      }
    } else {      
      setPurchaseDetailsData((prev) => ({
        ...prev,
        underWarrenty: false,
      }));
    }
  };

  const fetchOptions = async () => {
    try {
      setIsLoading(true);
      const token = getAuthToken("id_token"); // Example: Retrieve token from localStorage
      const config = {
        headers: {
          Authorization: `Bearer ${token}`, // Attach bearer token to request header
        },
      };

      //make nested try catch for error handling and dynamic API calls
      let assetOptions = {};
      try {
        assetOptions = await axios.get(
          `${MasterGraph}/api/masters/GetMasters/${companyId}/${facilityId}`,
          config
        );
      } catch (e) {
        console.log(e);
      }

      let vendorOptionsAPI = [];
      try {
        vendorOptionsAPI = await axios.get(
          `${MasterGraph}/api/GraphMaspomvendors/GetMaspomvendorsByFacilityId/${companyId}?Size=100000`,
          config
        );
      } catch (e) {
        console.log(e);
      }
      const vendorOptions = vendorOptionsAPI?.data?.map((item) => ({
        key: `option${item.id}`,
        label: `${item?.vendorcode} - ${item?.vendorname}`,
        id: item.id,
        vendorname: item?.vendorname,
      }));

      const url =
        assetType === FemsGraph
          ? `${FemsGraph}/api/femassettransfers`
          : `${BemsGraph}/api/bemassettransfers`;
      const transferCategoryOptionsAPI = await axios.get(`${url}`, config);

      const transferCategoryOptions = transferCategoryOptionsAPI.data?.map(
        (item, index) => ({
          key: `option${index + 1}`,
          label: item?.assettransfer,
          id: item?.id,
        })
      );

      const purchaseFlagOptionsAPI = await axios.get(
        `${assetType}/api/CommonLov/GetCommonLovList`,
        config
      );
      const femOrbem = assetType === FemsGraph ? "fem" : "bem";
      const purchaseFlagOptions =
        purchaseFlagOptionsAPI.data?.[`${femOrbem}assetpurchasecostflags`]?.map(
          (item) => ({
            key: `option${item.id}`,
            label: item.purchasecostflag,
            id: item.id,
            isvisible: item.isvisible,
          })
        ) || [];

      setPurchaseDetailsOptions((prev) => ({
        ...prev,
        purchaseFlagOptions: purchaseFlagOptions,
        vendorOptions: vendorOptions,
        assetTransferCategoryOptions: transferCategoryOptions,
      }));

      const transferDepartmentAPIList =
        assetOptions?.data?.masuserdepartmentdetails?.map((item, index) => ({
          id: item?.id,
          label: item?.departmentdesc,
          departmentcode: item?.departmentcode,
          key: `option${index + 1}`,
        }));

      let transferHospitalList = {};
      try {
        transferHospitalList = await axios.get(
          `${ifacSecurityURL}/api/GraphUserdetails/Locationbasedoncompantid/${companyId}`,
          config
        );
      } catch (e) {
        console.log(e);
      }
      setTransferHospitalList(
        transferHospitalList?.data?.map((item) => ({
          label: item?.facilityname,
          id: item?.id,
        }))
      );

      const assetTransferHospitalId =
        selectedGridData?.values?.assettransferlocationid;
      if (
        selectedGridData?.isEdit &&
        assetTransferHospitalId &&
        !purchaseDetailsData?.isDataSetInEditMode
      ) {
        if (assetTransferHospitalId) {
          setAssetTransferLabel("Asset Transfer Hospital");
          const transferHospital =
            transferHospitalList?.data?.find(
              (item) => item.id === assetTransferHospitalId
            ) ?? null;
          setPurchaseDetailsData((prev) => ({
            ...prev,
            assetTransferCategory: {
              label: "New Hospital",
              key: "option1",
              default: false,
              id: 1,
            },
            assetTransferDepartment: {
              id: transferHospital?.id,
              label: transferHospital?.facilityname,
            },
          }));
          let updateAssetTransferDepartment =
            mandatoryFieldsPurchaseDetails.filter(
              (item) => item.name === "assetTransferDepartment"
            );
          updateAssetTransferDepartment = updateAssetTransferDepartment?.map(
            (item) => {
              return {
                ...item,
                type: "Asset Transfer Hospital",
                isVisible: true,
                valueFilled: transferHospital?.id ? true : false,
              };
            }
          );
          const getOtherFields = mandatoryFieldsPurchaseDetails.filter(
            (item) => item.name !== "assetTransferDepartment"
          );
          const updateMandataryfields = [
            ...getOtherFields,
            ...updateAssetTransferDepartment,
          ];
          dispatch(
            mandatoryFields(
              updateMandataryfields,
              "UPDATE_PURCHASE_DETAILS_MANDATORY_FIELDS"
            )
          );
        }
      }

      //set the values in edit mode
      const FemORBem = assetType === FemsGraph ? "Fem" : "Bem";
      const purchasecostflagId =
        selectedGridData?.values?.[`${FemORBem}assetpurchasecostflagId`] ||
        selectedGridData?.values?.[`${femOrbem}assetpurchasecostflagId`];
      if (
        selectedGridData?.isEdit &&
        purchasecostflagId &&
        !purchaseDetailsData?.isDataSetInEditMode
      ) {
        const purchaseFlag = purchaseFlagOptions?.find(
          (item) =>
            item.id ===
            selectedGridData?.values?.[`${femOrbem}assetpurchasecostflagId`]
        );
        setPurchaseDetailsData((prev) => ({
          ...prev,
          purchaseFlag: purchaseFlag?.id
            ? {
                key: `option${purchaseFlag?.id}`,
                label: purchaseFlag?.label,
                id: purchaseFlag?.id,
                isvisible: purchaseFlag?.isvisible,
              }
            : "",
        }));
        setInitialPurchaseDetailsData((prev) => ({
          ...prev,
          purchaseFlag: purchaseFlag?.id
            ? {
                key: `option${purchaseFlag?.id}`,
                label: purchaseFlag?.label,
                id: purchaseFlag?.id,
                isvisible: purchaseFlag?.isvisible,
              }
            : "",
        }));
      }
      if (
        selectedGridData?.isEdit &&
        selectedGridData?.values?.vendorid &&
        !purchaseDetailsData?.isDataSetInEditMode
      ) {
        const vendorId = selectedGridData?.values?.vendorid; //set in fetchoptions method
        const vendor = vendorOptions.find((item) => item.id === vendorId);
        setPurchaseDetailsData((prev) => ({
          ...prev,
          vendor: vendor?.id ? { ...vendor, label: vendor?.vendorname } : "",
        }));
        setInitialPurchaseDetailsData((prev) => ({
          ...prev,
          vendor: vendor?.id ? { ...vendor, label: vendor?.vendorname } : "",
        }));
      }
      //set the values in edit mode
      if (
        selectedGridData?.isEdit &&
        selectedGridData?.values?.assettransferdepartmentid &&
        !purchaseDetailsData?.isDataSetInEditMode
      ) {
        const assettransferdepartmentid =
          selectedGridData?.values?.assettransferdepartmentid;
        const assetTransferDepartment = transferDepartmentAPIList.find(
          (item) => item.id === assettransferdepartmentid
        );

        setPurchaseDetailsData((prev) => ({
          ...prev,
          assetTransferCategory: {
            label: "New Department",
            key: "option2",
            default: false,
            id: 1,
          },
          assetTransferDepartment: assetTransferDepartment?.id
            ? {
                id: assetTransferDepartment?.id,
                label: assetTransferDepartment?.label,
              }
            : null,
        }));
        setInitialPurchaseDetailsData((prev) => ({
          ...prev,
          assetTransferCategory: {
            label: "New Department",
            key: "option2",
            default: false,
            id: 1,
          },
          assetTransferDepartment: assetTransferDepartment?.id
            ? {
                id: assetTransferDepartment?.id,
                label: assetTransferDepartment?.label,
              }
            : null,
        }));
        setAssetTransferLabel("Asset Transfer Department");
        setPurchaseDetailsOptions((prev) => ({
          ...prev,
          transferDepartmentList: transferDepartmentAPIList,
        }));

        let updateAssetTransferDepartment =
          mandatoryFieldsPurchaseDetails.filter(
            (item) => item.name === "assetTransferDepartment"
          );
        updateAssetTransferDepartment = updateAssetTransferDepartment?.map(
          (item) => {
            return {
              ...item,
              type: "Asset Transfer Department",
              isVisible: true,
              valueFilled: assettransferdepartmentid ? true : false,
            };
          }
        );
        const getOtherFields = mandatoryFieldsPurchaseDetails.filter(
          (item) => item.name !== "assetTransferDepartment"
        );
        const updateMandataryfields = [
          ...getOtherFields,
          ...updateAssetTransferDepartment,
        ];
        dispatch(
          mandatoryFields(
            updateMandataryfields,
            "UPDATE_PURCHASE_DETAILS_MANDATORY_FIELDS"
          )
        );
      }
      if (
        purchaseDetailsData?.assetTransferCategory?.label === "New Department"
      ) {
        setPurchaseDetailsOptions((prev) => {
          return {
            ...prev,
            transferDepartmentList: transferDepartmentAPIList,
          };
        });
      }

      setApiDepartmentList(transferDepartmentAPIList);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    setInitialPurchaseDetailsData(purchaseDetailsData);

  }, []);
  
  useEffect(()=>{
    
    checkIsUnderWarranty();
  },[purchaseDetailsData?.underWarrenty,purchaseDetailsData?.warrantyEndDate,purchaseDetailsData?.warrantyStartDate])

  useEffect(() => {
    if (
      purchaseDetailsData?.assetTransferCategory?.label === "New Department"
    ) {
      setAssetTransferLabel("Asset Transfer Department");
    }

    if (purchaseDetailsData?.assetTransferCategory?.label === "New Hospital") {
      setAssetTransferLabel("Asset Transfer Hospital");
    }

    if (
      selectedGridData &&
      selectedGridData?.isEdit &&
      !purchaseDetailsData?.isDataSetInEditMode
    ) {
      setIsLoading(true);
      checkIsUnderWarranty();
      const femOrbem = assetType === FemsGraph ? "fem" : "bem";
      const purchaseCost = selectedGridData?.values?.purchasecost || 0;
      const formattedPurchaseCost =
        purchaseCost === 0 ? 0 : parseFloat(purchaseCost).toFixed(2);
      let depreciationValue = selectedGridData?.values?.depreciationvalue;
      const unformattedwarrantyStartDate =
        selectedGridData?.values?.warrantystartdate;
      const unformattedwarrantyEndDate =
        selectedGridData?.values?.warrantyenddate;
      const unformattedcommissioningDate =
        selectedGridData?.values?.commissioningdate;
      const warrantyStartDate = unformattedwarrantyStartDate
        ? dayjs(unformattedwarrantyStartDate).format("DD/MM/YYYY")
        : ""; //convert date format
      const warrantyEndDate = unformattedwarrantyEndDate
        ? dayjs(unformattedwarrantyEndDate).format("DD/MM/YYYY")
        : "";
      const commissioningDate = unformattedcommissioningDate
        ? dayjs(unformattedcommissioningDate).format("DD/MM/YYYY")
        : "";
      const vendorId = selectedGridData?.values?.vendorid; //set in fetchoptions method
      const cumulativePartCost = selectedGridData?.values?.cumpartscost;
      const isunderwarrenty = selectedGridData?.values?.isunderwarranty;
      const accessories = selectedGridData?.values?.accessories;
      const purchaseFlagval =
        selectedGridData?.values?.[`${femOrbem}assetpurchasecostflag`];
      const purchaseFlag = {
        key: `option${purchaseFlagval?.id}`,
        label: purchaseFlagval?.purchasecostflag,
        id: purchaseFlagval?.id,
        isvisible: purchaseFlagval?.isvisible,
      };
      const purchaseorderno = selectedGridData?.values?.purchaseorderno;

      //calculate the depreciation value based on purchase cost and lifespan during edit page
      const lifeSpan = selectedGridData?.values?.lifespan || 0;
      if (!depreciationValue && purchaseCost && lifeSpan) {
        depreciationValue = purchaseCost / lifeSpan;
      }

      setInitialPurchaseDetailsData({
        ...purchaseDetailsData,
        purchaseCost: formattedPurchaseCost,
        depreciationValue: depreciationValue && depreciationValue?.toFixed(2),
        warrantyStartDate: warrantyStartDate,
        warrantyEndDate: warrantyEndDate,
        commissioningDate: commissioningDate,
        isDataSetInEditMode: true, //set this value true so that we can use it in edit mode only for not rendering the sideEffect of edit mode
        purchaseFlag: purchaseFlag?.id ? purchaseFlag : "",
        accessories: accessories,
        purchaseOrderNo: purchaseorderno,
        cumulativePartCost: cumulativePartCost,
      });
      setPurchaseDetailsData((prev) => {
        return {
          ...prev,
          purchaseCost: formattedPurchaseCost,
          depreciationValue: depreciationValue && depreciationValue?.toFixed(2),
          warrantyStartDate: warrantyStartDate,
          warrantyEndDate: warrantyEndDate,
          commissioningDate: commissioningDate,
          isDataSetInEditMode: true, //set this value true so that we can use it in edit mode only for not rendering the sideEffect of edit mode
          purchaseFlag: purchaseFlag?.id ? purchaseFlag : "",
          accessories: accessories,
          purchaseOrderNo: purchaseorderno,
          cumulativePartCost: cumulativePartCost,
          lastScheduledWorkOrder: purchaseDetails?.lastScheduledWorkOrder || "",
          lastUnscheduledWorkOrder: purchaseDetails?.lastUnscheduledWorkOrder || "",
          isunderwarrenty: isunderwarrenty,
          supplier:purchaseDetails?.supplier || selectedGridData?.values?.supplier || ""
        };
      });
    }
  }, [selectedGridData?.isEdit]);

  // This function is responsible for checking if all mandatory fields are filled or not
  const checkMandatoryValueFilled = (
    warrantyStartDate,
    warrantyEndDate,
    commissioningDate,
    purchaseCost,
    purchaseFlag,
    assetTransferDepartment,
    assetTransferCategory
  ) => {
    let updateStartDate = mandatoryFieldsPurchaseDetails.filter(
      (item) => item?.name === "warrantyStartDate"
    );
    let updateEndDate = mandatoryFieldsPurchaseDetails.filter(
      (item) => item?.name === "warrantyEndDate"
    );
    let updateCommissioningDate = mandatoryFieldsPurchaseDetails.filter(
      (item) => item?.name === "commissioningDate"
    );
    let updatePurchaseCost = mandatoryFieldsPurchaseDetails.filter(
      (item) => item?.name === "purchaseCost"
    );
    let updatePurchaseFlag = mandatoryFieldsPurchaseDetails.filter(
      (item) => item?.name === "purchaseFlag"
    );
    let updateAssetTransferDepartment = mandatoryFieldsPurchaseDetails.filter(
      (item) => item?.name === "assetTransferDepartment"
    );
    // let updateVendor = mandatoryFieldsPurchaseDetails.filter((item) => item?.name === 'vendor');

    if (
      !warrantyStartDate ||
      warrantyStartDate === "" ||
      warrantyStartDate === "Invalid Date"
    ) {
      updateStartDate = updateStartDate.map((item) => ({
        ...item,
        valueFilled: false,
      }));
    } else {
      updateStartDate = updateStartDate.map((item) => ({
        ...item,
        valueFilled: true,
      }));
    }
    if (
      !warrantyEndDate ||
      warrantyEndDate === "" ||
      warrantyEndDate === "Invalid Date"
    ) {
      updateEndDate = updateEndDate.map((item) => ({
        ...item,
        valueFilled: false,
      }));
    } else {
      updateEndDate = updateEndDate.map((item) => ({
        ...item,
        valueFilled: true,
      }));
    }
    if (
      !commissioningDate ||
      commissioningDate === "" ||
      commissioningDate === "Invalid Date"
    ) {
      updateCommissioningDate = updateCommissioningDate.map((item) => ({
        ...item,
        valueFilled: false,
      }));
    } else {
      updateCommissioningDate = updateCommissioningDate.map((item) => ({
        ...item,
        valueFilled: true,
      }));
    }
    if (!purchaseCost || purchaseCost === "") {
      updatePurchaseCost = updatePurchaseCost.map((item) => ({
        ...item,
        valueFilled: false,
      }));
    } else {
      updatePurchaseCost = updatePurchaseCost.map((item) => ({
        ...item,
        valueFilled: true,
      }));
    }
    if (!purchaseFlag || purchaseFlag === "") {
      updatePurchaseFlag = updatePurchaseFlag.map((item) => ({
        ...item,
        valueFilled: false,
      }));
    } else {
      updatePurchaseFlag = updatePurchaseFlag.map((item) => ({
        ...item,
        valueFilled: true,
      }));
    }
    if (!assetTransferDepartment || assetTransferDepartment === "") {
      updateAssetTransferDepartment = updateAssetTransferDepartment.map(
        (item) => ({ ...item, valueFilled: false })
      );
    } else {
      updateAssetTransferDepartment = updateAssetTransferDepartment.map(
        (item) => ({ ...item, valueFilled: true })
      );
    }

    //to hide and show the asset transfer field
    if (!assetTransferCategory?.label) {
      updateAssetTransferDepartment = updateAssetTransferDepartment.map(
        (item) => ({ ...item, isVisible: false })
      );
    }

    let updateMandataryfields = [
      ...updatePurchaseCost,
      ...updatePurchaseFlag,
      ...updateStartDate,
      ...updateEndDate,
      ...updateCommissioningDate,
      ...updateAssetTransferDepartment,
    ];

    dispatch(
      mandatoryFields(
        updateMandataryfields,
        "UPDATE_PURCHASE_DETAILS_MANDATORY_FIELDS"
      )
    );
  };

  useEffect(() => {
    const {
      warrantyStartDate,
      warrantyEndDate,
      commissioningDate,
      purchaseCost,
      purchaseFlag,
      assetTransferDepartment,
      assetTransferCategory,
    } = purchaseDetailsData;

    checkMandatoryValueFilled(
      warrantyStartDate,
      warrantyEndDate,
      commissioningDate,
      purchaseCost,
      purchaseFlag,
      assetTransferDepartment,
      assetTransferCategory
    );
  }, [purchaseDetailsData]);
  
  const handleOnChange = (value, name, isMandatoryField) => {
    // Handle the change event with the name and value
    if (name !== "vendor") {
      setPurchaseDetailsData((prev) => {
        return {
          ...prev,
          [name]: value,
        };
      });
      if (!value?.label) {
        setIsDataChanged(false);
      }
    } else {
      setIsDataChanged(true);
      setPurchaseDetailsData((prev) => {
        return {
          ...prev,
          [name]: value?.id ? { ...value, label: value?.vendorname } : "",
        };
      });
    }

    const checkWarrentyStartDate =
  name === "warrantyStartDate" ? value : purchaseDetailsData?.warrantyStartDate;
const checkWarrentyEndDate =
  name === "warrantyEndDate" ? value : purchaseDetailsData?.warrantyEndDate;

if (
  (name === "warrantyStartDate" || name === "warrantyEndDate") &&
  checkWarrentyStartDate &&
  checkWarrentyEndDate
) {
  // Parse the dates using dayjs for proper comparison
  const formattedWarrantyStartDate = dayjs(checkWarrentyStartDate, 'DD/MM/YYYY');
  const formattedWarrantyEndDate = dayjs(checkWarrentyEndDate, 'DD/MM/YYYY');

  if (formattedWarrantyStartDate.isAfter(formattedWarrantyEndDate)) {    
    // Invalid: Start date is after end date
    setError((prev) => ({
      ...prev,
      warrantyEndDate: true,
    }));
    setErrorMessage((prev) => ({
      ...prev,
      warrantyEndDate: "Please Enter a Valid Warranty End Date",
    }));
  } else {
    // Valid dates
    setError((prev) => ({
      ...prev,
      warrantyEndDate: false,
    }));
    setErrorMessage((prev) => ({
      ...prev,
      warrantyEndDate: "",
    }));
    setPurchaseDetailsData((prev) => ({
      ...prev,
      warrantyEndDate: checkWarrentyEndDate,
    }));
  }
} else {
  // Reset errors for other inputs
  setError((prev) => ({
    ...prev,
    [name]: false,
  }));
  setErrorMessage((prev) => ({
    ...prev,
    [name]: "",
  }));
}


    // if (selectedGridData?.isEdit) {
    //   if (name === "warrantyStartDate" || name === "warrantyEndDate") {
    //     checkIsUnderWarranty();
    //   }
    // }

    if (isMandatoryField) {
      setError((prev) => {
        const updatedError = {
          ...prev,
          [name]: false,
        };

        const isAllFalse = Object.values(updatedError).every(
          (value) => value === false
        );

        if (isAllFalse) {
          setIsFilledAllMandatoryField(false);
        }

        return updatedError;
      });
      setErrorMessage((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (name === "assetTransferCategory") {
      // Reset the error state and error message for assetTransferDepartment
      setError((prev) => ({
        ...prev,
        assetTransferDepartment: false,
      }));
      setErrorMessage((prev) => ({
        ...prev,
        assetTransferDepartment: "",
      }));

      //if the assetTransferCategory is changed then update the mandatoryFields(which is assetTranserDepartment)
      let allRequiredfields = mandatoryFieldsPurchaseDetails?.filter(
        (item) => item?.name !== "assetTransferDepartment"
      );
      let assetTransfer = mandatoryFieldsPurchaseDetails?.find(
        (item) => item?.name === "assetTransferDepartment"
      );
      let arraySample;
      if (value !== "" && value !== null && value !== undefined) {
        const label =
          value?.label === "New Hospital"
            ? "Asset Transfer Hospital"
            : "Asset Transfer Department";
        assetTransfer = { ...assetTransfer, isVisible: true, type: label };
        arraySample = [...allRequiredfields, assetTransfer];
      } else {
        assetTransfer = { ...assetTransfer, isVisible: false };
        arraySample = [...allRequiredfields, assetTransfer];
      }
      dispatch(
        mandatoryFields(arraySample, "UPDATE_PURCHASE_DETAILS_MANDATORY_FIELDS")
      );
    }
     //calculate depreciation value
     if (name === "purchaseCost") {
      // const formatNumericValue = (value) => {
      //   if (value === "") return "0"; // Default to 0 when input is cleared
      //   if (/^\d+$/.test(value)) return value.replace(/^0+/, ""); // Remove leading zeros
      //   return value; // Return valid numeric value (e.g., with decimals)
      // };

      const formatNumericValue = (value) => {
        if (value === "") return "0"; // Default to 0 when input is cleared
        if (/^\d+$/.test(value)) return value.replace(/^0+/, ""); // Remove leading zeros
        
        // Allow only two digits after the decimal point
        if (!isNaN(value)) {
          const [integerPart, decimalPart] = value.split(".");
          if (decimalPart) {
            return `${integerPart}.${decimalPart.substring(0, 2)}`;
          }
          return value;
        }
        
        return value; // Return valid numeric value (e.g., with decimals)
      };

      const formattedValue = formatNumericValue(value);
      let lifeSpanVal = selectedGridData?.values?.lifespan || 0;
      const calVal = formattedValue / lifeSpanVal;
      setPurchaseDetailsData((prev) => ({
        ...prev,
        purchaseCost: formattedValue,
        depreciationValue: calVal.toFixed(2),
      }));
    }

    if (name === "assetTransferCategory") {
      if (value?.label === "New Hospital") {
        setAssetTransferLabel("Asset Transfer Hospital");
        setPurchaseDetailsData((prev) => {
          return {
            ...prev,
            assetTransferDepartment: "",
          };
        });
        setPurchaseDetailsOptions((prev) => {
          return {
            ...prev,
            transferDepartmentList: transferHospitalList,
          };
        });
      } else if (value?.label === "New Department") {
        setAssetTransferLabel("Asset Transfer Department");
        setPurchaseDetailsData((prev) => {
          return {
            ...prev,
            assetTransferDepartment: "",
          };
        });
        setPurchaseDetailsOptions((prev) => {
          return {
            ...prev,
            transferDepartmentList: apiDepartmentList,
          };
        });
      } else {
        setAssetTransferLabel("");
        setPurchaseDetailsOptions((prev) => {
          return {
            ...prev,
            transferDepartmentList: [],
          };
        });
      }
    }
  };

  const handleValidationForRequiredFields = () => {

    let isValid = true;

    // Check if any error state is true
    if (Object.values(error).some((value) => value === true)) {
      isValid = false;
    }

    mandatoryFieldsPurchaseDetails.map((item) => {
      if (item.isVisible === true && item.valueFilled === false && !error[item.name] ) {
        setError((prev) => ({
          ...prev,
          [item.name]: true,
        }));
        setErrorMessage((prev) => ({
          ...prev,
          [item.name]: `${item.type} is required `,
        }));
        isValid = false;
      }
    });
    
    return isValid;
  };

  const clearInputs = () => {
    if (
      basicDetails?.rentalStatus?.label === "No" &&
      basicDetails?.assetClassification?.label !== "Building"
    ) {
      if (basicDetails?.assetClassification?.label === "Location") {
        const warrantyStartDate = "";
        const warrantyEndDate = "";
        const vendor = "";
        const updateValuesAfterClearInputs = {
          ...warrantyEndDate,
          ...warrantyStartDate,
          ...vendor,
          ...purchaseDetailsData,
        };
        setPurchaseDetailsData(updateValuesAfterClearInputs);
      }
      if (
        basicDetails?.assetClassification?.label === "System" ||
        basicDetails?.assetClassification?.label === "Asset"
      ) {
        const vendor = "";
        const updateValuesAfterClearInputs = {
          ...vendor,
          ...purchaseDetailsData,
        };
        setPurchaseDetailsData(updateValuesAfterClearInputs);
      }
    }
  };

  const changeDateFormat = (date) => {
    if (
      !date ||
      !dayjs(date, "DD/MM/YYYY").isValid() ||
      date === "Invalid Date" ||
      date == null
    ) {
      return "";
    }
    const convertedDate = dayjs(date, "DD/MM/YYYY").format("YYYY-MM-DD");
    const finalDate = `${convertedDate}T00:00:00`;
    return finalDate;
  };

  function mapKeyToInitialData(key) {
    const keyMap = {
      warrantyStartDate: "warrantyStartDate",
      warrantyEndDate: "warrantyEndDate",
      commissioningDate: "commissioningDate",
      warranty: "warranty",
      purchaseOrderNo: "purchaseOrderNo",
      purchaseCost: "purchaseCost",
      accessories: "accessories",
      cumulativePartCost: "cumulativePartCost",
      cumulativeLabourCost: "cumulativeLabourCost",
      lastUnscheduledWorkOrder: "lastUnscheduledWorkOrder",
      lastScheduledWorkOrder: "lastScheduledWorkOrder",
      assetTransferCategory: "assetTransferCategory",
      purchaseFlag: "purchaseFlag",
      assetTransferDepartment: "assetTransferDepartment",
      vendor: "vendor",
      depreciationValue: "depreciationValue",
      underWaranty: "underWaranty",
    };
    return keyMap[key] || key;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const isValid = handleValidationForRequiredFields();
    if (isValid) {
      const warrantyStartDateDBFormat = changeDateFormat(
        purchaseDetailsData.warrantyStartDate
      );
      const warrantyEndDateDBFormat = changeDateFormat(
        purchaseDetailsData.warrantyEndDate
      );
      const commissioningDateDBFormat = changeDateFormat(
        purchaseDetailsData.commissioningDate
      );
      clearInputs();
      const selectedData = selectedGridData?.values;
      const depreciationValue =
        purchaseDetailsData?.depreciationValue === undefined ||
        purchaseDetailsData?.depreciationValue === null
          ? 0
          : Number(purchaseDetailsData?.depreciationValue)?.toFixed(2);
      const commonParameters = {
        depreciationvalue: depreciationValue,
        // depreciationvalue: purchaseDetailsData?.depreciationValue === undefined ? 0 : purchaseDetailsData?.depreciationValue.toFixed(2),
        purchasecost: purchaseDetailsData?.purchaseCost,
        isunderwarranty: purchaseDetailsData?.underWarrenty,
        warrantystartdate: purchaseDetailsData?.warrantyStartDate
          ? warrantyStartDateDBFormat
          : "",
        warrantyenddate: purchaseDetailsData?.warrantyEndDate
          ? warrantyEndDateDBFormat
          : "",
        commissioningdate: purchaseDetailsData?.commissioningDate
          ? commissioningDateDBFormat
          : "",
        assettransferlocationid:
          assetTransferLabel === "Asset Transfer Hospital" &&
          purchaseDetailsData?.assetTransferDepartment?.id
            ? purchaseDetailsData?.assetTransferDepartment?.id
            : "",
        vendorid: purchaseDetailsData?.vendor?.id, //pass once the vendor api is given
        purchaseorderno: purchaseDetailsData?.purchaseOrderNo,
        accessories: purchaseDetailsData?.accessories,
        cumpartscost: purchaseDetailsData?.cumulativePartCost,
        assettransferdepartmentid:
          assetTransferLabel === "Asset Transfer Department" &&
          purchaseDetailsData?.assetTransferDepartment?.id
            ? purchaseDetailsData?.assetTransferDepartment?.id
            : "",
        supplier:purchaseDetailsData?.supplier || selectedData?.supplier || ""
      };
      const FemsParameter = {
        FemassetpurchasecostflagId: purchaseDetailsData?.purchaseFlag?.id
          ? purchaseDetailsData?.purchaseFlag?.id
          : "",
        FemassettransferId: purchaseDetailsData?.assetTransferCategory?.id,
      };
      const BemsParameter = {
        BemassetpurchasecostflagId: purchaseDetailsData?.purchaseFlag?.id
          ? purchaseDetailsData?.purchaseFlag?.id
          : "",
        BemassettransferId: purchaseDetailsData?.assetTransferCategory?.id,
      };
      const type = assetType === FemsGraph ? "FEMS" : "BEMS";
      let purchaseDetailsParameters = { ...commonParameters };
      type === "FEMS"
        ? (purchaseDetailsParameters = {
            ...commonParameters,
            ...FemsParameter,
          })
        : (purchaseDetailsParameters = {
            ...commonParameters,
            ...BemsParameter,
          });
      const isDataChanged = Object.keys(purchaseDetailsData).some((key) => {
        const initialKey = mapKeyToInitialData(key);
        return (
          purchaseDetailsData[key] !== initialPurchaseDetailsData[initialKey]
        );
      });
      if (isDataChanged) {
        try {
          const processingpercentage =
            selectedData?.processingpercentage > 75
              ? selectedData?.processingpercentage
              : 75;
          const isEditMode = purchaseDetails?.isAssetCreation ? false : true;
          const changedkeys = AssetRepository.compareObjects(
            initialPurchaseDetailsData,
            purchaseDetailsData
          );
          const response = assetCreateUpdateRepository?.updateAsset(
            selectedData,
            {
              ...purchaseDetailsParameters,
              processingpercentage: processingpercentage,
            },
            type,
            isEditMode,
            changedkeys
          );
          response.then((res) => {
            dispatch(gridValues({ values: res, isEdit: true, isView: false }));
            dispatch(
              updatePurchaseDetails(
                "UPDATE_PURCHASE_DETAILS",
                purchaseDetailsData
              )
            );
            setPage("Attachments");
            dispatch(
              mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED")
            );
            setInitialPurchaseDetailsData(purchaseDetailsData); // Update the initial state to the current state
          });
        } catch (err) {
          console.log(err);
        }
      } else {
        // If there is no data change and processing percentage is less than 75, update the processing percentage
        // If there is no data change and processing percentage is greater than 75, do not make api call
        const processingpercentage =
          selectedData?.processingpercentage > 75
            ? selectedData?.processingpercentage
            : 75;
        if (processingpercentage <= 75) {
          const response = assetCreateUpdateRepository?.updateAsset(
            selectedData,
            {
              ...purchaseDetailsParameters,
              processingpercentage: processingpercentage,
            },
            type
          );
          response.then((res) => {
            dispatch(gridValues({ values: res, isEdit: true, isView: false }));
            dispatch(
              updatePurchaseDetails(
                "UPDATE_PURCHASE_DETAILS",
                purchaseDetailsData
              )
            );
            setPage("Attachments");
            dispatch(
              mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED")
            );
            setInitialPurchaseDetailsData(purchaseDetailsData); // Update the initial state to the current state
          });
          setPage("Attachments");
          dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));
        } else {
          setPage("Attachments");
          dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));
        }
      }
    } else {
      setIsFilledAllMandatoryField(true);
    }
  };

  const assetValue =
    basicDetails?.assetValue || selectedGridData?.values?.assetNo || "";
  const isunderWarranty =
    purchaseDetailsData?.underWarrenty === true
      ? "Yes"
      : purchaseDetailsData?.underWarrenty === false
      ? "No"
      : "";
      
  return (
    <>
      <PageLoader open={isLoading} />
      <ComponentHeader
        mainHeader={findHeaderContent.mainHeader}
        subHeader={findHeaderContent?.subHeader}
        assetvalue={assetValue}
        requiredButton={findHeaderContent?.requiredButton}
        statusType={
          selectedGridData?.values?.isauthorized ? "authorized" : "unauthorized"
        }
      />
      <Box mt={{ xs: 1, md: 2 }}>
        <form onSubmit={handleSubmit} noValidate>
          {isFilledAllMandatoryField && (
            <Typography sx={{ color: "error.main" }}>
              Please Fill All Mandatory Fields(*)
            </Typography>
          )}
          <Grid container spacing={2}>
            {/* Row 1 */}
            <Grid item xs={12} md={4} sm={6}>
              <CustomTextField
                label="Purchase Order No."
                id="purchase-order-no."
                name="purchaseOrderNo"
                value={purchaseDetailsData?.purchaseOrderNo || ""}
                onChange={handleOnChange}
              />
            </Grid>
            <Grid item xs={12} md={4} sm={6}>
              <CustomTextField
                label="Purchase Cost in RM."
                id="purchase-cost"
                onlyNumbers={true}
                name="purchaseCost"
                error={error?.purchaseCost}
                errorMessage={errorMessage?.purchaseCost}
                value={purchaseDetailsData?.purchaseCost || 0}
                placeholder={"Enter a Number"}
                //Rental Asset selected ‘Yes’, then make it non mandatory
                isMandatoryField={
                  basicDetails?.assetClassification?.label === "Asset" ||
                  basicDetails?.assetClassification?.label === "System"
                    ? true
                    : false
                }
                onChange={handleOnChange}
                numbersLength={30}
                allowDecimal={true}
                textAlign="right"
              />
            </Grid>

            <Grid item xs={12} md={4} sm={6}>
              <CustomSelectInput
                id="purchase-flag"
                label="Purchase Flag"
                defaultValue="option1"
                name="purchaseFlag"
                value={purchaseDetailsData?.purchaseFlag || ""}
                error={error?.purchaseFlag}
                errorMessage={errorMessage?.purchaseFlag}
                //Rental Asset selected ‘Yes’, then make it non mandatory
                isMandatoryField={
                  basicDetails?.assetClassification?.label === "Asset" ||
                  basicDetails?.assetClassification?.label === "System"
                    ? true
                    : false
                }
                options={purchaseDetailsOptions.purchaseFlagOptions}
                onChange={handleOnChange}
              />
            </Grid>
            {/* under warranty filed should include when edit */}
            {/* Remove this Warranty Start Date and Warranty end Date while classification  – is selected as ‘Location’ */}
            {basicDetails?.assetClassification?.label !== "Location" && (
              <>
                <Grid item xs={12} md={4} sm={6}>
                  <CustomDatePicker
                    label="Warranty Start Date"
                    isStatrtDate={true}
                    id={"warranty-start-date"}
                    error={error?.warrantyStartDate}
                    errorMessage={errorMessage?.warrantyStartDate}
                    //Rental Asset selected ‘Yes’, then make it non mandatory
                    //if Rental Asset is 'No' and show Mandatory only for Asset & System
                    //for any other classifiction like asset or building then it is set to be non Mandatory field
                    isMandatoryField={
                      basicDetails?.rentalStatus?.label === "Yes"
                        ? false
                        : basicDetails?.assetClassification?.label ===
                            "Asset" ||
                          basicDetails?.assetClassification?.label === "System"
                        ? true
                        : false
                    }
                    name="warrantyStartDate"
                    value={purchaseDetailsData?.warrantyStartDate || ""}
                    onDateChange={handleOnChange}
                  />
                </Grid>

                <Grid item xs={12} md={4} sm={6}>
                  <CustomDatePicker
                    label="Warranty End Date"
                    error={error?.warrantyEndDate}
                    errorMessage={errorMessage?.warrantyEndDate}
                    //Rental Asset selected ‘Yes’, then make it non mandatory
                    //if Rental Asset is 'No' and show Mandatory only for Asset & System
                    //for any other classifiction like asset or building then it is set to be non Mandatory field
                    isMandatoryField={
                      basicDetails?.rentalStatus?.label === "Yes"
                        ? false
                        : basicDetails?.assetClassification?.label ===
                            "Asset" ||
                          basicDetails?.assetClassification?.label === "System"
                        ? true
                        : false
                    }
                  //  dissablePastDate={true}
                    id="warranty-end-date"
                    name="warrantyEndDate"
                    value={purchaseDetailsData?.warrantyEndDate || ""}
                    minimumDate={purchaseDetailsData?.warrantyStartDate}
                    onDateChange={handleOnChange}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={4} sm={6}>
              <CustomDatePicker
                label="Commissioning Date"
                error={error?.commissioningDate}
                errorMessage={errorMessage?.commissioningDate}
                //1.If Loaner/Rental Asset selected ‘Yes’, then make it non mandatory for all its  classifications.
                //2.Show Mandatory only for Asset & System
                //3.Remove mandatory if classification – is selected as ‘Building’ or ‘Location’.
                name="commissioningDate"
                isMandatoryField={
                  basicDetails?.rentalStatus?.label === "Yes"
                    ? false
                    : basicDetails?.assetClassification?.label === "Asset" ||
                      basicDetails?.assetClassification?.label === "System"
                    ? true
                    : false
                }
                id="commissioning-date*"
                value={purchaseDetailsData?.commissioningDate || ""}
                onDateChange={handleOnChange}
              />
            </Grid>

            <Grid item xs={12} md={4} sm={6}>
              <CustomTextField
                label="Accessories"
                id="accessories"
                name="accessories"
                value={purchaseDetailsData?.accessories || ""}
                onChange={handleOnChange}
              />
            </Grid>

            {!purchaseDetailsData.isAssetCreation && (
              <Grid item xs={12} md={4} sm={6}>
                <CustomTextField
                  id="underWaranty"
                  label="Under warranty"
                  name="underWaranty"
                  //change the values Values: Replacement Estimate, PO
                  // value={purchaseDetailsData?.underWaranty ? 'Yes' : 'No'}
                  value={isunderWarranty}
                  //Rental Asset selected ‘Yes’, then make it non mandatory
                  disabled={true}
                />
              </Grid>
            )}
            {(showassetSpecification === "Asset" ||
              showassetSpecification === "System") && (
              <Grid item xs={12} md={4} sm={6}>
                <CustomTextFieldWithSearch
                  label="Vendor"
                  id="vendor"
                  // error={error?.vendor}
                  // errorMessage={errorMessage?.vendor}
                  // isMandatoryField={true}
                  options={purchaseDetailsOptions?.vendorOptions}
                  name="vendor"
                  value={purchaseDetailsData?.vendor || purchaseDetails?.vendor || ""}
                  onChange={handleOnChange}
                />
              </Grid>
            )}

            {(showassetSpecification === "Asset" ||
              showassetSpecification === "System") && (selectedGridData?.values?.isoldsupplier) && (
              <Grid item xs={12} md={4} sm={6}>
                <CustomTextField
                  label="Old Supplier"
                  id="vendor"
                  name="vendor"
                  value={purchaseDetailsData?.supplier || selectedGridData?.values?.supplier || ""}
                  onChange={handleOnChange}
                  disabled={true}
                />
              </Grid>
            )}

            <Grid item xs={12} md={4} sm={6}>
              <CustomTextField
                label="Cumulative Part Cost in (RM)."
                id="cumulative-part-cost"
                name="cumulativePartCost"
                placeholder={""}
                disabled={true}
                value={purchaseDetailsData?.cumpartscost || ""}
                onChange={handleOnChange}
                textAlign="right"
                allowDecimal={true}
               
              />
            </Grid>

            {!purchaseDetailsData?.isAssetCreation && (
              <>
                <Grid item xs={12} md={4} sm={6}>
                  <CustomTextField
                    label="Last Scheduled Work Order"
                    disabled={true}
                    placeholder={""}
                    id="last-scheduled-work-order"
                    name="lastScheduledWorkOrder"
                    value={purchaseDetailsData?.lastScheduledWorkOrder || ""}
                    onChange={handleOnChange}
                  />
                </Grid>

                <Grid item xs={12} md={4} sm={6}>
                  <CustomTextField
                    label="Last Unscheduled Work Order"
                    disabled={true}
                    placeholder={""}
                    id="lastUnscheduledWorkOrder"
                    name="lastUnscheduledWorkOrder"
                    value={purchaseDetailsData?.lastUnscheduledWorkOrder || ""}
                    onChange={handleOnChange}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={4} sm={6}>
              <CustomTextField
                label="Depreciation value/ Year in RM"
                id="Deprecation-value"
                onlyNumbers={true}
                disabled={true}
                name="depreciationValue"
                value={purchaseDetailsData?.depreciationValue || ""}
                onChange={handleOnChange}
                textAlign="right"
                allowDecimal={true}
                
              />
            </Grid>

            <Grid item xs={12} md={4} sm={6}>
              <CustomSelectInput
                id="asset-transfer-category"
                label="Asset Transfer Category"
                name="assetTransferCategory"
                options={
                  purchaseDetailsOptions?.assetTransferCategoryOptions || ""
                }
                value={purchaseDetailsData.assetTransferCategory}
                onChange={handleOnChange}
              />
            </Grid>

            <Grid item xs={12} md={4} sm={6}>
              {assetTransferLabel !== "" && (
                <CustomSelectInput
                  id="asset-transfer-department"
                  label={assetTransferLabel}
                  error={error?.assetTransferDepartment}
                  errorMessage={errorMessage?.assetTransferDepartment}
                  isMandatoryField={assetTransferLabel !== "" ? true : false}
                  name="assetTransferDepartment"
                  options={purchaseDetailsOptions.transferDepartmentList}
                  value={purchaseDetailsData?.assetTransferDepartment || ""}
                  onChange={handleOnChange}
                />
              )}
            </Grid>
          </Grid>

          <Box>
            <BottomButtons
              isDataChanged={isDataChanged}
              page={page}
              buttonAttributes={buttonAttributes}
              params={purchaseDetailsData}
              notUpdateRedux={purchaseDetailsData?.isAssetCreation}
            />
          </Box>
        </form>
      </Box>
    </>
  );
};

export default PurchaseDetails;
